import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Auth } from './types';

// Only the username is kept in Redux state. It acts as the cache key for the
// RTK Query getUserDetails endpoint so that selectUser can look up the full
// UserProfile from the query cache without storing server data in the slice.
// This follows the tutorial's 'Cleaning Up the Slice' step
// Persisted to sessionStorage so the forced-password-change modal survives
// hard reloads (F5) and cannot be bypassed by refreshing the page.
// Cleared automatically when the browser tab is closed.
const REQUIRES_PASSWORD_CHANGE_KEY = '_rpc';

const initialState: Auth = {
  username: undefined,
  requiresPasswordChange:
    sessionStorage.getItem(REQUIRES_PASSWORD_CHANGE_KEY) === '1',
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Equivalent to the tutorial's setCurrentUsername / selectCurrentUsername.
    // Set on login/session restore; clear (pass undefined) on logout.
    setUsername: (state, action: PayloadAction<string | undefined>) => {
      state.username = action.payload;
    },
    // Sync the flag to sessionStorage so it persists across hard reloads.
    setRequiresPasswordChange: (state, action: PayloadAction<boolean>) => {
      state.requiresPasswordChange = action.payload;
      if (action.payload) {
        sessionStorage.setItem(REQUIRES_PASSWORD_CHANGE_KEY, '1');
      } else {
        sessionStorage.removeItem(REQUIRES_PASSWORD_CHANGE_KEY);
      }
    },
  },
});

export const { setUsername, setRequiresPasswordChange } = authSlice.actions;

export default authSlice.reducer;
